import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Alert, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import {
  formatters,
  parseNumericValue,
  makePeriodKey,
  normalizeBacktestResultStatus,
} from '../utils/backtestHelpers'; // Update import to include parseNumericValue and makePeriodKey
import { fetchWithAuth } from '../utils/apiHelpers';
import './BacktestTable.css';
import HistoryCard from './HistoryCard';

function BacktestTable({ botId, strategyId, revisionId, sortBy, sortOrder, periodFilter, getApiToken, setError }) {
  const [historyError, setHistoryError] = useState("");
  const [expandedResult, setExpandedResult] = useState(null);
  const [backtestHistory, setBacktestHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [sortConfig, setSortConfig] = useState({
    key: 'createdAt',
    direction: 'desc'
  });

  const handleSelectBacktest = (result) => {
    setExpandedResult(expandedResult?.id === result.id ? null : result);
  };

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <FontAwesomeIcon icon={faSort} className="sort-icon" />;
    }
    return sortConfig.direction === 'asc' 
      ? <FontAwesomeIcon icon={faSortUp} className="sort-icon active" />
      : <FontAwesomeIcon icon={faSortDown} className="sort-icon active" />;
  };

  const fetchBacktestHistory = useCallback(async (retryCount = 0) => {
    if (!strategyId) return;
    
    const maxRetries = 3;
    const retryDelay = Math.min(1000 * Math.pow(2, retryCount), 5000); // Exponential backoff

    try {
      setIsLoadingHistory(true);
      setHistoryError("");
      const token = await getApiToken();
      // Use the correctly imported fetchWithAuth
      const data = await fetchWithAuth(token, `/backtests/${strategyId}/stats`);

      const processedResults = data.backtests
        .map(result => ({
          ...result,
          id: result.id,
          startDate: result.startDate,
          endDate: result.endDate,
          cagrAnnualReturn: parseNumericValue(result.cagrAnnualReturn),
          maxDrawdown: parseNumericValue(result.maxDrawdown),
          totalReturn: parseNumericValue(result.totalReturn),
          sharpe: parseNumericValue(result.sharpe),
          sortino: parseNumericValue(result.sortino),
          romad: parseNumericValue(result.romad),
          createdAt: result.createdAt,
          updatedAt: result.updatedAt
        }));
      
      setBacktestHistory(processedResults);

      // Auto-select most recent backtest (regardless of status)
      // This ensures failed backtests are shown so users see what happened
      if (processedResults.length > 0) {
        const sortedByDate = [...processedResults].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        const mostRecent = sortedByDate[0];
        setExpandedResult(mostRecent);
      }
    } catch (err) {
      console.error("History error:", err);
      
      // Parse error message to determine error type
      const errorMessage = err.message || "Unknown error";
      let userFriendlyMessage = "Error fetching backtest history";
      let shouldRetry = false;

      // Detect HTML error responses (backend endpoint doesn't exist)
      if (errorMessage.includes("<!DOCTYPE html>") || 
          errorMessage.includes("Cannot GET /backtests/") ||
          errorMessage.includes("Invalid JSON response")) {
        userFriendlyMessage = "Backtest history endpoint is not available. This feature may not be implemented yet.";
        shouldRetry = false; // NO RETRY - endpoint doesn't exist
      } 
      // HTTP 404 errors (endpoint not found)
      else if (errorMessage.includes("HTTP 404") || errorMessage.includes("404")) {
        userFriendlyMessage = "No backtest history found for this strategy yet.";
        shouldRetry = false; // NO RETRY - 404s are permanent
        // Set empty history for 404s instead of showing error
        setBacktestHistory([]);
        return;
      }
      // Permission errors
      else if (errorMessage.includes("HTTP 403") || errorMessage.includes("403")) {
        userFriendlyMessage = "You don't have permission to view this strategy's backtest history.";
        shouldRetry = false; // NO RETRY - permission errors won't resolve
      }
      // Only retry for genuine network/connectivity issues
      else if (errorMessage.includes("Failed to fetch") || 
               errorMessage.includes("TypeError") ||
               errorMessage.includes("ECONNREFUSED") ||
               errorMessage.includes("Network error") ||
               errorMessage.includes("timeout")) {
        userFriendlyMessage = "Network connection issue. Please check your internet connection.";
        shouldRetry = retryCount < maxRetries;
      }
      // Server errors that might resolve
      else if (errorMessage.includes("HTTP 500") || errorMessage.includes("500")) {
        userFriendlyMessage = "Server error while fetching backtest history. Please try again.";
        shouldRetry = retryCount < maxRetries;
      }
      else {
        // Generic error - don't retry unknown errors
        userFriendlyMessage = "Error fetching backtest history: Server configuration issue";
        shouldRetry = false;
      }

      // Attempt retry with exponential backoff (only for retryable errors)
      if (shouldRetry) {
        console.log("Retrying backtest table fetch in " + retryDelay + "ms");
        setHistoryError(userFriendlyMessage + " (Retry " + (retryCount + 1) + "/" + maxRetries + ")");
        
        setTimeout(() => {
          fetchBacktestHistory(retryCount + 1);
        }, retryDelay);
        return;
      }

      // Final error - no more retries
      setHistoryError(userFriendlyMessage);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [strategyId, getApiToken, setError]);

  useEffect(() => {
    fetchBacktestHistory();
  }, [fetchBacktestHistory]);

  if (isLoadingHistory) {
    return (
      <div className="backtest-history-loading">
        <Spinner animation="border" role="status" className="spinner" />
        <span className="loading-text">Loading history...</span>
      </div>
    );
  }

  if (historyError) {
    return <Alert variant="danger">{historyError}</Alert>;
  }

  if (!backtestHistory?.length) {
    return (
      <div className="backtest-base-card">
        <div className="no-history-message">No backtest history available for this strategy.</div>
      </div>
    );
  }

  const filteredResults = filterBacktestResults(backtestHistory, periodFilter, sortConfig.key, sortConfig.direction);
    
  return (
    <div className="backtest-base-card">
      <div className="backtest-results-layout">
        {expandedResult && (
          <div className="expanded-backtest-card" id="expanded-card">
            <HistoryCard
              result={expandedResult}
              isExpanded={true}
              onToggleExpand={() => handleSelectBacktest(expandedResult)}
              getApiToken={getApiToken}
              setError={setError}
              botId={botId}
              revisionId={revisionId}
            />
          </div>
        )}
        <div className="backtest-table-container">
          <table className="backtest-table">
            <thead>
              <tr>
                <th 
                  className="run-date-column"
                  onClick={() => handleSort('createdAt')}
                >
                  Run Date {getSortIcon('createdAt')}
                </th>
                <th 
                  className="version-column"
                  onClick={() => handleSort('version')}
                >
                  Revision Version {getSortIcon('version')}
                </th>
                <th 
                  className="start-date-column"
                  onClick={() => handleSort('startDate')}
                >
                  Start Date {getSortIcon('startDate')}
                </th>
                <th 
                  className="end-date-column"
                  onClick={() => handleSort('endDate')}
                >
                  End Date {getSortIcon('endDate')}
                </th>
                <th 
                  className="annual-return-column"
                  onClick={() => handleSort('annualReturn')}
                >
                  Annual Return {getSortIcon('annualReturn')}
                </th>
                <th 
                  className="drawdown-column"
                  onClick={() => handleSort('maxDrawdown')}
                >
                  Max Drawdown {getSortIcon('maxDrawdown')}
                </th>
                <th 
                  className="sharpe-column"
                  onClick={() => handleSort('sharpe')}
                >
                  Sharpe {getSortIcon('sharpe')}
                </th>
                <th onClick={() => handleSort('status')}>
                  Status {getSortIcon('status')}
                </th>
                <th className="action-column">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredResults.map((result) => {
                const isCompleted = normalizeBacktestResultStatus(result) === 'completed';
                const hasMetrics = isCompleted && result.cagrAnnualReturn != null;
                  
                return (
                  <tr 
                    key={result.id}
                    className={expandedResult?.id === result.id ? 'selected-row' : ''}
                    onClick={() => handleSelectBacktest(result)}
                  >
                    <td className="run-date-column">
                      {formatters.dateTime(result.createdAt)}
                    </td>
                    <td className="version-column">
                      {result.version || 'N/A'}
                    </td>
                    <td className="start-date-column">
                      {formatters.date(result.startDate)}
                    </td>
                    <td className="end-date-column">
                      {formatters.date(result.endDate)}
                    </td>
                    <td className={`annual-return-column ${hasMetrics ? (result.cagrAnnualReturn >= 0 ? 'positive' : 'negative') : ''}`}>
                      {hasMetrics ? formatters.percent(result.cagrAnnualReturn) : 'N/A'}
                    </td>
                    <td className={`drawdown-column ${hasMetrics ? 'negative' : ''}`}>
                      {hasMetrics ? formatters.percent(result.maxDrawdown) : 'N/A'}
                    </td>
                    <td className={`sharpe-column ${hasMetrics ? (result.sharpe >= 0 ? 'positive' : 'negative') : ''}`}>
                      {hasMetrics ? formatters.number(result.sharpe) : 'N/A'}
                    </td>
                    <td>
                      {result.status.split('_')
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(' ')}
                    </td>
                    <td className="action-column">
                      <button 
                        className="view-backtest-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectBacktest(result);
                        }}
                        aria-label="View this backtest"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const filterBacktestResults = (results, periodFilter, sortBy, sortOrder) => {    
  let finalList = [...results];
  
  if (periodFilter !== "all") {
    finalList = finalList.filter(
      (item) => makePeriodKey(item.startDate, item.endDate) === periodFilter
    );
  }
  
  finalList.sort((a, b) => {
    let compare = 0;
    let aVal, bVal;
    
    switch (sortBy) {
      case "annualReturn":
        aVal = parseNumericValue(a.cagrAnnualReturn);
        bVal = parseNumericValue(b.cagrAnnualReturn);
        compare = (aVal || 0) - (bVal || 0);
        break;
        
      case "sharpe":
        aVal = parseNumericValue(a.sharpe);
        bVal = parseNumericValue(b.sharpe);
        compare = (aVal || 0) - (bVal || 0);
        break;
        
      case "sortino":
        aVal = parseNumericValue(a.sortino);
        bVal = parseNumericValue(b.sortino);
        compare = (aVal || 0) - (bVal || 0);
        break;
        
      case "maxDrawdown":
        aVal = parseNumericValue(a.maxDrawdown);
        bVal = parseNumericValue(b.maxDrawdown);
        compare = (aVal || 0) - (bVal || 0);
        break;
        
      case "totalReturn":
        aVal = parseNumericValue(a.totalReturn);
        bVal = parseNumericValue(b.totalReturn);
        compare = (aVal || 0) - (bVal || 0);
        break;
        
      case "version":
        aVal = a.version || 0;
        bVal = b.version || 0;
        compare = aVal - bVal;
        break;

      case "startDate":
      case "endDate":
      case "createdAt":
        try {
          const aDate = a[sortBy] ? new Date(a[sortBy]).getTime() : 0;
          const bDate = b[sortBy] ? new Date(b[sortBy]).getTime() : 0;
          compare = aDate - bDate;
        } catch (err) {
          console.error(`Error comparing ${sortBy} dates:`, err);
          compare = 0;
        }
        break;
        
      default:
        try {
          const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          compare = bDate - aDate;
        } catch (err) {
          console.error("Error in default date comparison:", err);
          compare = 0;
        }
    }
    
    return sortOrder === "desc" ? -compare : compare;
  });
  
  return finalList;
};

export default BacktestTable;
